import React, { useMemo } from 'react';
import QRCode from 'react-qr-code';
import Barcode from 'react-barcode';
import { formatInStoreTimezone } from '../../utils/dateUtils';

// Tyro customer receipts normally contain a masked PAN. Keep this defensive
// guard at the print boundary so an unexpected terminal payload can never put
// a full card number on a POS receipt.
const maskPotentialPan = (value = '') => String(value).replace(
  /\b(?:\d[ -]?){12,18}\d\b/g,
  (candidate) => {
    const digits = candidate.replace(/\D/g, '');
    return digits.length >= 13 && digits.length <= 19
      ? `************${digits.slice(-4)}`
      : candidate;
  }
);

const getTyroReceiptLine = (receiptText, pattern) =>
  maskPotentialPan(receiptText)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find((line) => pattern.test(line));

const getTyroReceiptValue = (receiptText, labelPattern) => {
  const line = getTyroReceiptLine(receiptText, labelPattern);
  return line ? line.replace(labelPattern, '').trim() : '';
};

const getTyroCardDetails = (copy) => {
  const receiptText = String(copy?.customerReceipt || '');
  const status = getTyroReceiptLine(receiptText, /\b(?:APPROVED|DECLINED|CANCELLED)\b/i)
    ?.match(/\b(APPROVED|DECLINED|CANCELLED)\b/i)?.[1]
    ?.toUpperCase();
  const card = getTyroReceiptValue(receiptText, /^CARD\s*:\s*/i)
    || getTyroReceiptLine(
      receiptText,
      /\b(?:VISA|MASTERCARD|AMEX|AMERICAN EXPRESS|EFTPOS|DINERS|JCB|UNIONPAY)\b/i
    );
  const terminalId = getTyroReceiptValue(receiptText, /^TERMINAL\s+ID\s*:\s*/i);
  const transactionReference = getTyroReceiptValue(
    receiptText,
    /^TRANSACTION\s+REF(?:ERENCE)?\s*:\s*/i
  ) || copy?.tyroTransactionId || '';
  const authorisation = getTyroReceiptValue(
    receiptText,
    /^AUTH(?:ORISATION|ORIZATION)\s+(?:NO|NUMBER|CODE|ID)\s*:\s*/i
  ) || getTyroReceiptLine(
    receiptText,
    /\b(?:AUTH(?:ORISATION|ORIZATION)?(?:\s*(?:CODE|NO|NUMBER|ID))?|APPROVAL\s*(?:CODE|NO|NUMBER|ID)?)\b/i
  );

  return {
    status: status || 'APPROVED',
    card,
    terminalId,
    transactionReference,
    authorisation,
  };
};

const ReceiptContent = React.forwardRef(({ sale, store, tyroReceiptCopies = [] }, ref) => {
  const receiptStore = useMemo(() => {
    if (store) return store;
    if (sale && typeof sale.store === 'object') return sale.store;
    return null;
  }, [sale, store]);

  const timezone = receiptStore?.timezone || 'Australia/Melbourne';
  const hasGst = (sale?.gstAmount ?? 0) > 0;
  const receiptSettings = receiptStore?.receiptSettings || {};
  const shouldPrintStoreName = receiptSettings.printStoreName !== false;
  const qrSetting = receiptSettings.printQrCode || 'Membership';
  const shouldShowQr = qrSetting !== 'Disabled';
  const qrMessage = receiptSettings.qrCodeNotice || 'Thank you for shopping! Scan to Join Membership';
  const footerText = receiptSettings.footerText || receiptSettings.additionalText || '';
  const formattedAddress = useMemo(() => {
    const address = receiptStore?.address;
    if (!address) return '';
    const parts = [address.street, address.suburb, address.state, address.postcode].filter(Boolean);
    return parts.join(', ');
  }, [receiptStore]);
  const businessName = (receiptStore?.companyName || receiptStore?.name || sale?.store?.companyName || sale?.store?.name || '').trim();
  const abn = receiptStore?.businessNumber || sale?.store?.businessNumber || '';
  const cashierUserId = sale?.user?.userId || '';
  const registerName = sale?.till?.name || '';
  const hasTaxFreeItems = sale?.items?.some(item => item.gstApplicable === false);
  const totalItems = sale?.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
  const manualDiscountTotal = sale?.items?.reduce(
    (sum, item) => sum + Number(item.manualDiscount || 0),
    0
  ) || 0;
  const paymentAllocations = Array.isArray(sale?.paymentAllocations)
    ? sale.paymentAllocations.filter((allocation) => Number(allocation?.amount || 0) > 0)
    : [];
  const hasCashPayment = sale?.paymentMethod === 'Cash'
    || paymentAllocations.some((allocation) => allocation?.method === 'Cash');
  const appliedPromotionDetails = Array.isArray(sale?.appliedPromotionDetails)
    ? sale.appliedPromotionDetails.filter((detail) => Number(detail?.discountAmount || 0) > 0)
    : [];
  const customerTyroReceiptCopies = Array.isArray(tyroReceiptCopies)
    ? tyroReceiptCopies.filter((copy) => copy?.customerReceipt)
    : [];

  const formatMoney = (value = 0) => Number(value || 0).toFixed(2);
  const rawReceiptNumber = sale?.receiptNumber ? String(sale.receiptNumber) : '';
  const displayReceiptNumber = rawReceiptNumber ? rawReceiptNumber.padStart(6, '0') : '';
  const barcodeValue = displayReceiptNumber;
  const isBarcodeReady = barcodeValue.length > 0;

  if (!sale) return null;

  return (
    <div ref={ref} id="receipt-to-print" className="text-gray-900 p-6 font-mono text-sm bg-white max-w-xs mx-auto leading-snug">
      <div className="text-center space-y-1">
        {shouldPrintStoreName && businessName && (
          <h1 className="text-lg font-bold tracking-wide">{businessName}</h1>
        )}
        {hasGst && abn && <p className="uppercase text-xs tracking-wide">TAX INVOICE / ABN {abn}</p>}
        {!hasGst && abn && <p className="text-xs">ABN {abn}</p>}
        {formattedAddress && <p className="text-xs">{formattedAddress}</p>}
        {receiptStore?.phone && <p className="text-xs">Ph: {receiptStore.phone}</p>}
      </div>

      <div className="border-t border-b border-dashed border-gray-400 my-3 py-2 text-xs space-y-1">
        <div className="text-xs font-mono whitespace-nowrap">
          Time: {formatInStoreTimezone(sale.createdAt, 'dd-MM-yyyy hh:mm a', timezone)}
        </div>
        <div className="flex justify-between">
          <span>Receipt #:</span>
          <span>{displayReceiptNumber}</span>
        </div>
        {/* {sale._id && (
          <div className="flex justify-between">
            <span>Transaction ID:</span>
            <span>{sale._id}</span>
          </div>
        )} */}
        {cashierUserId && (
          <div className="flex justify-between">
            <span>User ID:</span>
            <span>{cashierUserId}</span>
          </div>
        )}
        {registerName && (
          <div className="flex justify-between">
            <span>POS:</span>
            <span>{registerName}</span>
          </div>
        )}
      </div>

      <div>
        <div className="flex text-xs font-semibold uppercase border-b border-gray-300 pb-1">
          <span className="flex-1">Item Name</span>
          <span className="w-12 text-right">Qty</span>
          <span className="w-20 text-right">Price($)</span>
        </div>
        <div className="divide-y divide-dashed divide-gray-300">
          {sale.items.map(item => {
            const promotionDiscount = Number(item.discount || 0);
            const manualDiscount = Number(item.manualDiscount || 0);
            const lineTotal = (item.price * item.quantity) - promotionDiscount;
            const displayedDiscount = promotionDiscount + manualDiscount;
            return (
              <div key={item.product} className="flex py-1 text-xs">
                <span className="flex-1 pr-2">
                  {item.name}{item.gstApplicable === false ? ' *' : ''}
                  {displayedDiscount > 0 ? (
                    <span className="block text-[10px] text-gray-500">Discount: -${formatMoney(displayedDiscount)}</span>
                  ) : item.manualDiscountReason && (
                    <span className="block text-[10px] text-gray-500">Discount: Applied</span>
                  )}
                </span>
                <span className="w-12 text-right">{item.quantity}</span>
                <span className="w-20 text-right">${formatMoney(lineTotal)}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 space-y-1 text-xs">
        <div className="flex justify-between">
          <span>Total Items</span>
          <span>{totalItems}</span>
        </div>
        {sale.totalDiscount > 0 && (
          <div className="flex justify-between">
            <span>Discounts</span>
            <span>- ${formatMoney(sale.totalDiscount)}</span>
          </div>
        )}
        {manualDiscountTotal > 0 && (
          <div className="flex justify-between text-gray-500">
            <span>Manual discounts included</span>
            <span>- ${formatMoney(manualDiscountTotal)}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span>GST Included In Total</span>
          <span>${formatMoney(sale.gstAmount)}</span>
        </div>
        {sale.rounding !== 0 && (
          <div className="flex justify-between">
            <span>Cash Rounding</span>
            <span>{sale.rounding > 0 ? '+' : '-'}${formatMoney(Math.abs(sale.rounding))}</span>
          </div>
        )}
        <div className="flex justify-between font-bold text-base border-t border-dashed border-gray-400 pt-2">
          <span>Total</span>
          <span>${formatMoney(sale.finalAmount)}</span>
        </div>
        <div className="flex justify-between font-semibold text-sm">
          <span>Total Paid</span>
          <span>${formatMoney(sale.finalAmount)}</span>
        </div>
        <div className="flex justify-between">
          <span>Payment Method</span>
          <span>{paymentAllocations.length > 1 ? 'Split' : sale.paymentMethod}</span>
        </div>
        {paymentAllocations.length > 0 && paymentAllocations.map((allocation, index) => (
          <div key={`${allocation.allocationId || allocation.method}-${index}`} className="flex justify-between text-[11px] text-gray-600">
            <span>
              {allocation.method}
              {allocation.source ? ` (${allocation.source})` : ''}
            </span>
            <span>${formatMoney(allocation.amount)}</span>
          </div>
        ))}
        {hasCashPayment && sale.cashTendered != null && (
          <div className="flex justify-between">
            <span>Cash Tendered</span>
            <span>${formatMoney(sale.cashTendered)}</span>
          </div>
        )}
        {hasCashPayment && sale.cashChange != null && (
          <div className="flex justify-between">
            <span>Change Given</span>
            <span>${formatMoney(sale.cashChange)}</span>
          </div>
        )}
        {hasTaxFreeItems && <p className="text-[10px] mt-1">* Indicates Tax Free Items</p>}
      </div>

      {customerTyroReceiptCopies.length > 0 && (
        <div className="mt-4 border-t border-dashed border-gray-400 pt-2 text-xs">
          <p className="font-semibold uppercase tracking-wide">Card Payment Details</p>
          {customerTyroReceiptCopies.map((copy, index) => {
            const details = getTyroCardDetails(copy);
            return (
              <div key={`${copy.tyroTransactionId || 'tyro'}-${index}`} className="mt-2">
                {customerTyroReceiptCopies.length > 1 && (
                  <p className="text-[10px] uppercase text-gray-600">
                    Card payment {index + 1}
                  </p>
                )}
                <div className="space-y-1 text-[11px]">
                  <div className="flex justify-between gap-3"><span>Status</span><span>{details.status}</span></div>
                  {details.card && <div className="break-words">{details.card}</div>}
                  {details.terminalId && (
                    <div className="flex justify-between gap-3"><span>Terminal ID</span><span>{details.terminalId}</span></div>
                  )}
                  {details.transactionReference && (
                    <div className="flex justify-between gap-3"><span>Transaction Ref</span><span>{details.transactionReference}</span></div>
                  )}
                  {details.authorisation && (
                    <div className="flex justify-between gap-3"><span>Authorisation No</span><span>{details.authorisation}</span></div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {appliedPromotionDetails.length > 0 && (
        <div className="mt-4 space-y-1 text-xs border-t border-dashed border-gray-400 pt-2">
          <p className="font-semibold uppercase tracking-wide">Applied Promotions</p>
          {appliedPromotionDetails.map((detail, index) => (
            <div key={`${detail.promoId || detail.name}-${index}`} className="flex justify-between gap-3">
              <span className="flex-1">
                {detail.name}
                {detail.slab?.rangeLabel ? ` (${detail.slab.rangeLabel})` : ''}
                {detail.slab?.rewardType === 'PERCENTAGE' ? ` ${detail.slab.rewardValue}%` : ''}
                {detail.slab?.rewardType === 'FIXED_AMOUNT'
                  ? ` $${formatMoney(detail.slab.rewardValue)}${detail.slab.rewardApplication === 'PER_UNIT' ? ' each' : ''}`
                  : ''}
              </span>
              <span>- ${formatMoney(detail.discountAmount)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="text-center mt-4 text-xs space-y-3">
        {isBarcodeReady && (
          <div className="flex flex-col items-center space-y-1" data-barcode-value={barcodeValue}>
            <Barcode value={barcodeValue} format="CODE128" displayValue={false} height={50} width={1.4} margin={0} />
            <p>* {displayReceiptNumber} *</p>
          </div>
        )}
      </div>

      {shouldShowQr && (
        <div className="mt-4 flex flex-col items-center space-y-2">
          {qrMessage && <p className="text-xs text-center">{qrMessage}</p>}
          <div data-qrcode-value="https://urbanrain.com.au/">
            <QRCode value="https://urbanrain.com.au/" size={128} />
          </div>
        </div>
      )}

      {footerText && (
        <div className="mt-4 text-center text-xs space-y-1">
          {footerText.split('\n').map((line, idx) => (
            <p key={idx}>{line}</p>
          ))}
        </div>
      )}

    </div>
  );
});

export default ReceiptContent;
